import type { Product, ProductSubscription } from "../types";

export interface EnrolledProduct {
  subscription: ProductSubscription;
  product: Product;
  productStatus?: boolean;
}

export interface PendingSubscriptionProduct {
  subscription: ProductSubscription;
  product: Product;
}

const SUBSCRIPTION_PRODUCTS_FRAGMENT = `
  products {
    productStatus
    product {
      uuid
      name
      description
    }
  }
`;

export function getSubscriptionProducts(
  subscription: ProductSubscription,
): EnrolledProduct[] {
  return (subscription.products ?? [])
    .filter((entry) => entry.product?.uuid)
    .map((entry) => ({
      subscription,
      product: entry.product,
      productStatus: entry.productStatus,
    }));
}

export function flattenSubscriptionProducts(
  subscriptions: ProductSubscription[],
): EnrolledProduct[] {
  return subscriptions.flatMap(getSubscriptionProducts);
}

export function flattenApprovedProducts(
  subscriptions: ProductSubscription[],
): EnrolledProduct[] {
  return flattenSubscriptionProducts(subscriptions).filter(
    (entry) => entry.productStatus !== false,
  );
}

export function flattenPendingProducts(
  subscriptions: ProductSubscription[],
): PendingSubscriptionProduct[] {
  return flattenSubscriptionProducts(subscriptions)
    .filter((entry) => entry.productStatus === false)
    .map(({ subscription, product }) => ({ subscription, product }));
}

export function subscriptionIncludesProduct(
  subscription: ProductSubscription,
  productUuid: string,
): boolean {
  return getSubscriptionProducts(subscription).some(
    (entry) => entry.product.uuid === productUuid,
  );
}

export function findEnrollmentByProductUuid(
  subscriptions: ProductSubscription[],
  productUuid: string,
  approvedOnly = false,
): EnrolledProduct | undefined {
  const entries = approvedOnly
    ? flattenApprovedProducts(subscriptions)
    : flattenSubscriptionProducts(subscriptions);
  return entries.find((entry) => entry.product.uuid === productUuid);
}

export { SUBSCRIPTION_PRODUCTS_FRAGMENT };
